// Types for Advanced Estimate Sections
// Minimal subset needed for displaying sections in the payment-site

export type EstimateSectionType =
  | 'scope_of_work'
  | 'materials'
  | 'labor'
  | 'timeline'
  | 'exclusions'
  | 'warranty';

// Timeline milestone
export interface TimelineMilestone {
  id: string;
  name: string;
  date: string | null;
  description?: string;
}

// Timeline data
export interface EstimateTimeline {
  startDate: string | null;
  endDate: string | null;
  milestones: TimelineMilestone[];
}

// Text section content
export interface TextSectionContent {
  type: 'text';
  text: string;
}

// Timeline section content
export interface TimelineSectionContent {
  type: 'timeline';
  data: EstimateTimeline;
}

// Materials section content (hidden from customers)
export interface MaterialsSectionContent {
  type: 'materials';
  items: any[];
  includeInTotal: boolean;
}

// Labor section content (hidden from customers)
export interface LaborSectionContent {
  type: 'labor';
  items: any[];
  includeInTotal: boolean;
}

export type SectionContent =
  | TextSectionContent
  | TimelineSectionContent
  | MaterialsSectionContent
  | LaborSectionContent;

// Individual section
export interface EstimateSection {
  id: string;
  type: EstimateSectionType;
  enabled: boolean;
  sortOrder: number;
  content: SectionContent;
}

// Container for all sections
export interface AdvancedEstimateSections {
  sections: EstimateSection[];
  sectionOrder: string[];
}

// Type guards
export const isTextContent = (content: SectionContent): content is TextSectionContent => {
  return content.type === 'text';
};

export const isTimelineContent = (content: SectionContent): content is TimelineSectionContent => {
  return content.type === 'timeline';
};

// Section titles for display
export const SECTION_TITLES: Record<EstimateSectionType, string> = {
  scope_of_work: 'Scope of Work',
  materials: 'Materials',
  labor: 'Labor',
  timeline: 'Project Timeline',
  exclusions: 'Exclusions & Assumptions',
  warranty: 'Warranty & Terms'
};

// Sections visible to customers (excludes financial details)
export const CUSTOMER_VISIBLE_SECTIONS: EstimateSectionType[] = [
  'scope_of_work',
  'timeline',
  'exclusions',
  'warranty'
];
